import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaPlus, FaEye, FaEdit, FaTrash, FaCopy, FaEnvelope, 
  FaCheckCircle, FaTimes, FaSearch, FaFilter,
  FaSpinner, FaFileAlt, FaBuilding, FaUser, FaCalendarAlt,
  FaMoneyBillWave, FaClock, FaExclamationTriangle,
  FaFilePdf, FaBan, FaUndo, FaUpload, FaCheckDouble, FaHashtag,
  FaBox, FaTruck, FaGem, FaLeaf, FaClipboardList, FaChartLine, FaPercent,
  FaSort, FaSortUp, FaSortDown
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import { quotes, clients, users } from '../api';
import { effectiveQuoteStatus } from '../utils/quoteStatus';

function QuotesPage({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quotesList, setQuotesList] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [user, setUser] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [clientsList, setClientsList] = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approving, setApproving] = useState(false);
  const [approveStartDate, setApproveStartDate] = useState('');
  const [loadconFile, setLoadconFile] = useState(null);
  const [loadconRef, setLoadconRef] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [stats, setStats] = useState({
    total: 0, draft: 0, sent: 0, approved: 0, rejected: 0, cancelled: 0, client_request: 0, client_accepted: 0,
    expired: 0, active_value: 0, active_gp: 0, active_margin_pct: 0, active_count: 0,
    gp_count: 0, gp_revenue: 0
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    loadQuotes();
    loadClients();
    loadSalespersons();
  }, []);

  useEffect(() => { applyFilters(); }, [searchTerm, filterStatus, filterType, filterDateFrom, filterDateTo, quotesList, sortField, sortDir]);

  const loadQuotes = async () => {
    setLoading(true);
    try {
      // GET /api/quotes paginates at 100 rows a request, and the status counters
      // (plus the search/filter) are computed from the rows we hold. A single
      // request therefore froze every figure at 100 once the book passed that
      // many quotes, so walk every page instead of just the first.
      const PAGE = 100;
      let all = [];
      for (let page = 1; page <= 500; page++) {
        const response = await quotes.getAll({ limit: PAGE, page });
        if (!response.success) { toast.error(response.error || 'Failed to load quotes'); break; }
        const batch = response.quotes || [];
        all = all.concat(batch);
        if (batch.length < PAGE) break;
      }
      setQuotesList(all);
      calculateStats(all);
    } catch (err) { toast.error('Failed to load quotes'); }
    finally { setLoading(false); }
  };

  const loadClients = async () => { try { const r = await clients.getAll(); if (r.success) setClientsList(r.clients || []); } catch {} };
  const loadSalespersons = async () => { try { const r = await users.getAll(); if (r.success) setSalespersons(r.users?.filter(u => u.role === 'admin' || u.role === 'super_admin') || []); } catch {} };

  const getQuoteCost = (q) => {
    // For abnormal/general quotes, cost is in line items
    if (q.items && q.items.length > 0) {
      return q.items.reduce((sum, item) => sum + (parseFloat(item.cost_price) || 0) * (parseFloat(item.quantity) || 1), 0);
    }
    // For minerals/agri quotes, cost is in subbie_quote (per MT × tonnage)
    if (parseFloat(q.subbie_quote) > 0) {
      return parseFloat(q.subbie_quote) * (parseFloat(q.tonnage) || 1);
    }
    // For container quotes, cost is derived from rate card
    if (parseFloat(q.subbie_quote) > 0) return parseFloat(q.subbie_quote);
    // Fallback: use subbie_quote or 0
    return parseFloat(q.subbie_quote) || 0;
  };

  const calculateStats = (list) => {
    const s = { total: 0, draft: 0, sent: 0, approved: 0, rejected: 0, cancelled: 0, client_request: 0, client_accepted: 0, expired: 0, active_value: 0, active_gp: 0, active_margin_pct: 0, active_count: 0, gp_count: 0, gp_revenue: 0 };
    list.forEach(q => {
      s.total++;
      const shown = effectiveQuoteStatus(q);
      s[shown] = (s[shown] || 0) + 1;
      // An EXPIRED quote is no longer active - it cannot be accepted as it stands.
      if (shown !== 'rejected' && shown !== 'cancelled' && shown !== 'expired') {
        s.active_count++;
        const revenue = parseFloat(q.grand_total) || 0;
        const cost = getQuoteCost(q);
        s.active_value += revenue;
        // Gross Profit and margin are only meaningful where a COST was captured.
        // Quotes with no cost still count in Active Value (the pipeline) but must
        // not flatter GP or skew the margin, so they are left out of both.
        if (revenue > 0 && cost > 0) {
          s.active_gp += (revenue - cost);
          s.gp_revenue += revenue;
          s.gp_count++;
        }
      }
    });
    // TRUE BLENDED MARGIN = total gross profit / total revenue of the quotes we have
    // a cost for (NOT the average of each quote's percentage, which let a small quote
    // swing the number as hard as a big one).
    s.active_margin_pct = s.gp_revenue > 0 ? (s.active_gp / s.gp_revenue) * 100 : 0;
    setStats(s);
  };

  const applyFilters = () => {
    let f = [...quotesList];
    if (searchTerm) { const t = searchTerm.toLowerCase(); f = f.filter(q => (q.quote_number||'').toLowerCase().includes(t) || (q.customer_name||'').toLowerCase().includes(t)); }
    if (filterStatus !== 'all') f = f.filter(q => effectiveQuoteStatus(q) === filterStatus);
    if (filterType !== 'all') f = f.filter(q => q.quote_type === filterType);
    if (filterDateFrom) f = f.filter(q => q.created_at >= filterDateFrom);
    if (filterDateTo) f = f.filter(q => q.created_at <= filterDateTo + 'T23:59:59');
    // Sort
    f.sort((a, b) => {
      let va, vb;
      if (sortField === 'customer_name') { va = (a.customer_name||'').toLowerCase(); vb = (b.customer_name||'').toLowerCase(); }
      else if (sortField === 'quote_type') { va = (a.quote_type||''); vb = (b.quote_type||''); }
      else if (sortField === 'grand_total') { va = parseFloat(a.grand_total)||0; vb = parseFloat(b.grand_total)||0; }
      else if (sortField === 'status') { va = (effectiveQuoteStatus(a)||''); vb = (effectiveQuoteStatus(b)||''); }
      else { va = (a.created_at||''); vb = (b.created_at||''); }
      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    setFilteredQuotes(f);
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <FaSort className="text-gray-300 ml-1" size={10} />;
    return sortDir === 'asc' ? <FaSortUp className="text-navy ml-1" size={10} /> : <FaSortDown className="text-navy ml-1" size={10} />;
  };

  const getTypeLabel = (type) => {
    const labels = { minerals: '⛏️ Minerals', agri: '🌾 Agri', abnormal: '🚛 Abnormal', container: '📦 Container', general: '📋 General' };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = { minerals: 'bg-amber-100 text-amber-700', agri: 'bg-green-100 text-green-700', abnormal: 'bg-purple-100 text-purple-700', container: 'bg-yellow-100 text-yellow-700', general: 'bg-blue-100 text-blue-700' };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-700', sent: 'bg-blue-100 text-blue-700', approved: 'bg-green-100 text-green-600',
      rejected: 'bg-red-100 text-red-700', cancelled: 'bg-gray-100 text-gray-500', client_request: 'bg-orange-100 text-orange-700',
      client_accepted: 'bg-yellow-100 text-yellow-700', credited: 'bg-red-100 text-red-700', expired: 'bg-red-100 text-red-700',
      // A VOIDED quote is one whose invoice was cancelled before any money changed hands. It must not
      // read "credited" — nothing was credited, because nothing was ever paid.
      void: 'bg-gray-200 text-gray-700'
    };
    return `px-2 py-1 rounded-full text-xs font-semibold ${badges[status] || 'bg-gray-100 text-gray-700'}`;
  };

  const getStatusLabel = (status) => {
    const labels = { draft: '📝 Draft', sent: '📤 Sent', approved: '✅ Approved', rejected: '❌ Rejected', cancelled: '🚫 Cancelled', client_request: '📩 Client Request', client_accepted: '✅ Client Accepted', credited: '🧾 Credited', expired: '⛔ Expired', void: '🚫 Voided' };
    return labels[status] || status;
  };

  const formatCurrency = (amount) => { if (!amount) return 'R0.00'; return 'R' + Number(amount).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
  const formatShortCurrency = (amount) => {
    if (!amount) return 'R0';
    const n = Number(amount);
    if (n >= 1000000) return 'R' + (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return 'R' + Math.round(n / 1000) + 'K';
    return 'R' + Math.round(n);
  };
  const formatDate = (d) => { if (!d) return 'N/A'; return new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }); };

  const handleViewQuote = (q) => navigate(`/quotes/${q.id}`);
  const handleEditQuote = (q) => { if (q.quote_type === 'container') navigate(`/quotes/edit/${q.id}/container`); else navigate(`/quotes/edit/${q.id}`); };
  const handleSendQuote = (q) => {
    const subjects = { minerals: 'Quotation for Mineral Transport Services', agri: 'Quotation for Agricultural Transport Services', abnormal: 'Quotation for Abnormal Load Transport', container: 'Quotation for Container Transport Services', general: 'Quotation for General Transport Services' };
    window.location.href = `mailto:${q.contact_email||''}?subject=${encodeURIComponent(subjects[q.quote_type]||'Quotation')}&body=${encodeURIComponent(`Dear ${q.customer_name||'Customer'},\n\nPlease find attached our quotation ${q.quote_number}.\n\nQuote: ${q.quote_number}\nType: ${q.quote_type}\nTotal: ${formatCurrency(q.grand_total)}\n\nKind regards,\nOne Purpose Logistics`)}`;
    toast.info('Email client opened. Attach the PDF manually.');
  };

  const handleDeleteClick = (q) => { setDeleteTarget(q); setShowDeleteModal(true); };
  const confirmDelete = async () => { if (!deleteTarget) return; setDeleting(true); try { const r = await quotes.delete(deleteTarget.id); if (r.success) { toast.success(`Deleted ${deleteTarget.quote_number}`); setShowDeleteModal(false); setDeleteTarget(null); loadQuotes(); } else toast.error(r.error); } catch { toast.error('Failed'); } finally { setDeleting(false); } };
  const handleCancelClick = (q) => { setCancelTarget(q); setShowCancelModal(true); };
  const confirmCancel = async () => { if (!cancelTarget) return; try { await quotes.cancel(cancelTarget.id); toast.success(`Cancelled`); setShowCancelModal(false); loadQuotes(); } catch { toast.error('Failed'); } };
  const handleRejectClick = (q) => { setRejectTarget(q); setRejectReason(''); setShowRejectModal(true); };
  const confirmReject = async () => { if (!rejectTarget) return; setRejecting(true); try { await quotes.reject(rejectTarget.id, { reason: rejectReason }); toast.success(`Rejected`); setShowRejectModal(false); setRejectReason(''); loadQuotes(); } catch { toast.error('Failed'); } finally { setRejecting(false); } };
  const handleApproveClick = (q) => { setApproveTarget(q); setApproveStartDate(new Date().toISOString().split('T')[0]); setLoadconFile(null); setLoadconRef(''); setShowApproveModal(true); };

  const confirmApprove = async () => {
    if (!approveTarget) return; setApproving(true);
    try {
      let loadconUrl = null;
      if (loadconFile) {
        const fd = new FormData(); fd.append('file', loadconFile); fd.append('type', 'loadcon');
        const ur = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/uploads`, { method: 'POST', headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }, body: fd });
        const ud = await ur.json(); if (ud.success) loadconUrl = ud.url || ud.file_url;
      }
      const r = await quotes.approve(approveTarget.id, { requested_start_date: approveStartDate, loadcon_file_url: loadconUrl, loadcon_ref_number: loadconRef || null });
      if (r.success) { toast.success(`Approved! Job created.`); setShowApproveModal(false); setApproveTarget(null); loadQuotes(); }
      else toast.error(r.error || 'Failed');
    } catch { toast.error('Failed'); }
    finally { setApproving(false); }
  };

  const handleDuplicate = async (q) => { setActionLoading(q.id); try { const r = await quotes.duplicate(q.id); if (r.success) { toast.success(`Duplicated as ${r.quote.quote_number}`); loadQuotes(); } else toast.error(r.error); } catch { toast.error('Failed'); } finally { setActionLoading(null); } };

  if (loading) return (<AdminLayout activeTab="quotes" onLogout={onLogout} user={user}><div className="flex items-center justify-center h-64"><FaSpinner className="animate-spin text-4xl text-green-500" /></div></AdminLayout>);

  const paginated = filteredQuotes.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const totalPages = Math.ceil(filteredQuotes.length / pageSize);

  return (
    <AdminLayout activeTab="quotes" onLogout={onLogout} user={user}>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-navy">📄 Quotations</h1>
          <p className="text-sm text-gray-500">Manage all your quotes and proposals</p>
        </div>
        <div className="flex gap-3">
          <Link to="/quotes/create" className="flex items-center gap-2 px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-green hover:text-navy transition-colors shadow-md"><FaPlus /> New Quote</Link>
          <Link to="/quotes/create/container" className="flex items-center gap-2 px-4 py-2 bg-yellow-400 text-navy rounded-lg text-sm font-bold hover:bg-yellow-300 transition-colors shadow-md">📦 Container Quote</Link>
        </div>
      </div>

      {/* Status counts */}
      <div className="grid grid-cols-4 md:grid-cols-9 gap-3 mb-3">
        {[
          { label: 'All', value: stats.total, color: '#64748b', icon: <FaFileAlt /> },
          { label: 'Draft', value: stats.draft, color: '#94a3b8', icon: <FaEdit /> },
          { label: 'Sent', value: stats.sent, color: '#3b82f6', icon: <FaEnvelope /> },
          { label: 'Approved', value: stats.approved, color: '#22c55e', icon: <FaCheckCircle /> },
          { label: 'Rejected', value: stats.rejected, color: '#ef4444', icon: <FaTimes /> },
          { label: 'Cancelled', value: stats.cancelled, color: '#6b7280', icon: <FaBan /> },
          { label: 'Requests', value: stats.client_request, color: '#f59e0b', icon: <FaUser /> },
          { label: 'Accepted', value: stats.client_accepted, color: '#8b5cf6', icon: <FaCheckDouble /> },
          { label: 'Expired', value: stats.expired, color: '#dc2626', icon: <FaExclamationTriangle /> }
        ].map(s => (
          <div key={s.label} className="bg-white rounded-lg p-3 text-center border shadow-sm" style={{ borderColor: '#E8ECEF', borderLeft: `4px solid ${s.color}` }}>
            <div className="text-xs text-gray-400 flex items-center justify-center gap-1 mb-1">{s.icon} {s.label}</div>
            <div className="text-lg font-bold" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>
      {/* Financial KPIs */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label: 'Active Value', value: formatShortCurrency(stats.active_value), color: '#059669', icon: <FaMoneyBillWave /> },
          { label: 'Gross Profit', value: formatShortCurrency(stats.active_gp), color: '#2563eb', icon: <FaChartLine /> },
          { label: 'Blended Margin', value: stats.active_margin_pct.toFixed(1) + '%', color: '#8b5cf6', icon: <FaPercent /> }
        ].map(s => (
          <div key={s.label} className="bg-white rounded-lg p-4 text-center border shadow-sm" style={{ borderColor: '#E8ECEF', borderLeft: `4px solid ${s.color}` }}>
            <div className="text-xs text-gray-400 flex items-center justify-center gap-1 mb-1">{s.icon} {s.label}</div>
            <div className="text-xl font-bold" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border p-4 mb-6 shadow-sm" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[200px]"><FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" /><input type="text" placeholder="Search quotes or customers..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm" style={{ borderColor: '#E8ECEF' }} /></div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E8ECEF' }}><option value="all">All Statuses</option><option value="draft">Draft</option><option value="sent">Sent</option><option value="approved">Approved</option><option value="rejected">Rejected</option><option value="client_request">Client Request</option><option value="client_accepted">Client Accepted</option><option value="credited">Credited</option><option value="void">Voided</option><option value="expired">Expired</option></select>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E8ECEF' }}><option value="all">All Types</option><option value="minerals">⛏️ Minerals</option><option value="agri">🌾 Agri</option><option value="abnormal">🚛 Abnormal</option><option value="container">📦 Container</option><option value="general">📋 General</option></select>
          <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E8ECEF' }} title="From date" />
          <span className="text-gray-400 text-sm">to</span>
          <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E8ECEF' }} title="To date" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-hidden shadow-sm" style={{ borderColor: '#E8ECEF' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50 border-b" style={{ borderColor: '#E8ECEF' }}>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Quote #</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-navy" onClick={() => handleSort('customer_name')}>Customer <SortIcon field="customer_name" /></th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-navy" onClick={() => handleSort('quote_type')}>Type <SortIcon field="quote_type" /></th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-navy" onClick={() => handleSort('grand_total')}>Amount <SortIcon field="grand_total" /></th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-navy" onClick={() => handleSort('status')}>Status <SortIcon field="status" /></th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
            </tr></thead>
            <tbody>
              {paginated.length === 0 ? (<tr><td colSpan={7} className="text-center py-12 text-gray-400"><div className="text-4xl mb-2">📋</div>No quotes found</td></tr>) :
                paginated.map(q => (
                  <tr key={q.id} className="border-b hover:bg-gray-50 cursor-pointer" style={{ borderColor: '#E8ECEF' }} onClick={() => handleViewQuote(q)}>
                    <td className="px-4 py-3 font-medium text-navy">{q.quote_number}</td>
                    <td className="px-4 py-3"><div className="font-medium text-navy">{q.customer_name || q.customer?.company_name || 'N/A'}</div>{(q.contact_person || q.customer?.contact_person) && <div className="text-xs text-gray-400">{q.contact_person || q.customer?.contact_person}</div>}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full font-bold ${getTypeColor(q.quote_type)}`}>{getTypeLabel(q.quote_type)}</span></td>
                    <td className="px-4 py-3 text-right font-medium">{formatCurrency(q.grand_total)}</td>
                    <td className="px-4 py-3"><span className={getStatusBadge(effectiveQuoteStatus(q))}>{getStatusLabel(effectiveQuoteStatus(q))}</span></td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(q.created_at)}</td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex justify-start gap-1">
                        <button onClick={() => handleViewQuote(q)} className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-blue-600 flex items-center justify-center" title="View"><FaEye size={14} /></button>
                        {(q.status === 'draft' || q.status === 'sent') && (<button onClick={() => handleEditQuote(q)} className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-blue-600 flex items-center justify-center" title="Edit"><FaEdit size={14} /></button>)}
                        {(q.status === 'draft' || q.status === 'client_request') && (<button onClick={() => handleSendQuote(q)} className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-amber-600 flex items-center justify-center" title="Send"><FaEnvelope size={14} /></button>)}
                        {(q.status === 'sent' || q.status === 'client_accepted') && (<button onClick={() => handleApproveClick(q)} className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-green-600 flex items-center justify-center" title="Approve & Create Job"><FaCheckCircle size={14} /></button>)}
                        {q.status === 'sent' && (<button onClick={() => handleRejectClick(q)} className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-red-600 flex items-center justify-center" title="Reject"><FaTimes size={14} /></button>)}
                        <button onClick={() => handleDuplicate(q)} className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-purple-600 flex items-center justify-center" title="Duplicate">{actionLoading === q.id ? <FaSpinner className="animate-spin" size={14} /> : <FaCopy size={14} />}</button>
                        {['draft', 'sent'].includes(q.status) && (<button onClick={() => handleCancelClick(q)} className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-orange-600 flex items-center justify-center" title="Cancel"><FaBan size={14} /></button>)}
                        <button onClick={() => handleDeleteClick(q)} className="w-8 h-8 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-red-600 flex items-center justify-center" title="Delete"><FaTrash size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t" style={{ borderColor: '#E8ECEF' }}>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 border rounded text-sm disabled:opacity-30 hover:bg-gray-50">← Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (<button key={p} onClick={() => setCurrentPage(p)} className={`px-3 py-1 border rounded text-sm ${p === currentPage ? 'bg-navy text-white' : 'hover:bg-gray-50'}`}>{p}</button>))}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border rounded text-sm disabled:opacity-30 hover:bg-gray-50">Next →</button>
          </div>
        )}
      </div>

      {/* APPROVE MODAL */}
      {showApproveModal && approveTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowApproveModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-navy mb-2 flex items-center gap-2"><FaCheckDouble className="text-green-500" /> Approve & Create Job</h2>
            <p className="text-sm text-gray-500 mb-4">Quote: <strong>{approveTarget.quote_number}</strong> — {approveTarget.customer_name}</p>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1"><FaCalendarAlt className="inline mr-1" /> Start Date / Load Date *</label><input type="date" value={approveStartDate} onChange={e => setApproveStartDate(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1"><FaUpload className="inline mr-1" /> Loadcon / Purchase Order</label><input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setLoadconFile(e.target.files[0])} className="w-full text-sm" />{loadconFile && <p className="text-xs text-green-600 mt-1">✅ {loadconFile.name}</p>}<p className="text-xs text-gray-400 mt-1">Upload the client's PO or load confirmation.</p></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1"><FaHashtag className="inline mr-1" /> Loadcon Ref # (Optional)</label><input type="text" value={loadconRef} onChange={e => setLoadconRef(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. PO-2026-001" /></div>
              <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-700"><strong>What happens:</strong> A job will be created with the quote details.</div>
            </div>
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
              <button onClick={() => setShowApproveModal(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button
                onClick={confirmApprove}
                disabled={approving}
                className="px-6 py-2 rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                style={{ backgroundColor: '#00A86B', color: '#FFFFFF', border: 'none', cursor: approving ? 'default' : 'pointer' }}
                onMouseEnter={(e) => { if (!approving) e.currentTarget.style.backgroundColor = '#00995E'; }}
                onMouseLeave={(e) => { if (!approving) e.currentTarget.style.backgroundColor = '#00A86B'; }}
              >{approving ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}{approving ? 'Creating Job...' : 'Approve & Create Job'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && rejectTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowRejectModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-red-600 mb-2">Reject Quote</h2>
            <p className="text-sm text-gray-600 mb-4">Reject <strong>{rejectTarget.quote_number}</strong>?</p>

            <label className="block text-sm font-medium text-gray-700 mb-2">Reason for rejection</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {['Too expensive', 'Chose another supplier', 'No longer needed', 'Scope changed', 'Other'].map(r => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRejectReason(r)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${rejectReason === r ? 'bg-red-100 text-red-700 border-red-300' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}
                >
                  {r}
                </button>
              ))}
            </div>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              rows={2}
              placeholder="Add more detail..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-red-400 mb-4"
            />

            <div className="flex justify-end gap-3">
              <button onClick={() => setShowRejectModal(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={confirmReject} disabled={rejecting} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">{rejecting ? <FaSpinner className="animate-spin" size={14} /> : <FaTimes size={14} />}{rejecting ? 'Rejecting...' : 'Reject Quote'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDeleteModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6"><h2 className="text-xl font-bold text-red-600 mb-2">Delete Quote</h2><p className="text-sm text-gray-600 mb-4">Delete <strong>{deleteTarget.quote_number}</strong>? This cannot be undone.</p><div className="flex justify-end gap-3"><button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 border rounded-lg text-sm">Cancel</button><button onClick={confirmDelete} disabled={deleting} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">{deleting ? 'Deleting...' : 'Delete'}</button></div></div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowCancelModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6"><h2 className="text-xl font-bold text-orange-600 mb-2">Cancel Quote</h2><p className="text-sm text-gray-600 mb-4">Cancel <strong>{cancelTarget.quote_number}</strong>?</p><div className="flex justify-end gap-3"><button onClick={() => setShowCancelModal(false)} className="px-4 py-2 border rounded-lg text-sm">Back</button><button onClick={confirmCancel} className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700">Cancel Quote</button></div></div>
        </div>
      )}
    </AdminLayout>
  );
}

export default QuotesPage;